import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";

import type {
  MarketplaceOrder,
  OrderLineVideo,
  OrderScriptRow,
} from "../types/marketplace.types";
import { effectiveScriptFulfillmentStatus } from "./effective-script-fulfillment";
import {
  filledOrderLineVideos,
  orderLineVideoKey,
  orderLineVideos,
} from "./order-line-videos";
import { orderLineVideoFulfillmentStatus } from "./video-display-status";

/** No Status filter selected. */
export const NO_STATUS_FILTER: ReadonlySet<MarketplaceFulfillmentStatus> = new Set();

/** The status a filled video shows as its chip: the server's, else the client mapping. */
export function filledVideoDisplayStatus(
  script: Pick<OrderScriptRow, "fulfillmentStatus" | "videos">,
  video: OrderLineVideo,
): MarketplaceFulfillmentStatus | null {
  return (
    video.fulfillmentStatus ??
    orderLineVideoFulfillmentStatus(script.fulfillmentStatus, video, {
      videos: script.videos,
    })
  );
}

/** A filled video whose displayed status is selected; empty slots never match. */
export function videoMatchesStatusFilter(
  script: OrderScriptRow,
  video: OrderLineVideo,
  statuses: ReadonlySet<MarketplaceFulfillmentStatus>,
): boolean {
  const status = filledVideoDisplayStatus(script, video);
  return status !== null && statuses.has(status);
}

/**
 * A line matches a Status filter on its adjusted status or on any filled
 * video's status. Empty slots are skipped, so an unfilled slot never matches.
 */
export function scriptMatchesStatusFilter(
  script: OrderScriptRow,
  statuses: ReadonlySet<MarketplaceFulfillmentStatus>,
): boolean {
  if (statuses.size === 0) return true;
  if (statuses.has(effectiveScriptFulfillmentStatus(script))) return true;
  return filledOrderLineVideos(script).some((video) =>
    videoMatchesStatusFilter(script, video, statuses),
  );
}

export interface StatusFilterVideoView {
  /** Filled videos the filter keeps visible. */
  visibleVideos: OrderLineVideo[];
  /** Filled videos the filter hides. */
  hiddenVideoCount: number;
  /** At least one filled video matched, so `visibleVideos` are the matches. */
  matchesOnVideos: boolean;
}

/**
 * Which filled videos a Status filter keeps under a matched line. A line that
 * matches only on its own status has no video to single out, so none are hidden.
 */
export function statusFilterVideoView(
  script: OrderScriptRow,
  statuses: ReadonlySet<MarketplaceFulfillmentStatus>,
): StatusFilterVideoView {
  const filled = filledOrderLineVideos(script);
  const matching =
    statuses.size === 0
      ? []
      : filled.filter((video) =>
          videoMatchesStatusFilter(script, video, statuses),
        );
  if (matching.length === 0) {
    return { visibleVideos: filled, hiddenVideoCount: 0, matchesOnVideos: false };
  }
  return {
    visibleVideos: matching,
    hiddenVideoCount: filled.length - matching.length,
    matchesOnVideos: true,
  };
}

/** Render state of one Orders tracker order under a Status filter. */
export interface OpsTrackerFilterView {
  /** A Status filter is on. */
  active: boolean;
  /** The user opened the whole order from a hidden-rows note. */
  revealed: boolean;
}

function isNarrowing(view: OpsTrackerFilterView): boolean {
  return view.active && !view.revealed;
}

/** Lines the tracker shows: every line, or the matched ones while a filter narrows. */
export function opsTrackerLines(
  order: Pick<MarketplaceOrder, "scripts">,
  view: OpsTrackerFilterView,
): OrderScriptRow[] {
  if (!isNarrowing(view)) return order.scripts;
  return order.scripts.filter(
    (script) => script.statusFilterMatch?.matched !== false,
  );
}

/**
 * Video rows the tracker lists: every slot, or the matched filled videos while
 * a filter narrows. A line that matched only on its own status keeps all its
 * filled videos; empty slots stay hidden.
 */
export function opsTrackerVideos(
  script: OrderScriptRow,
  view: OpsTrackerFilterView,
): OrderLineVideo[] {
  if (!isNarrowing(view)) return orderLineVideos(script);
  const filled = filledOrderLineVideos(script);
  const keys = script.statusFilterMatch?.videoKeys;
  if (!keys) return filled;
  const matched = new Set(keys);
  return filled.filter((video) => matched.has(orderLineVideoKey(video)));
}

/** Filled videos the filter hides under a line; empty slots are not counted. */
export function opsTrackerHiddenVideoCount(
  script: OrderScriptRow,
  view: OpsTrackerFilterView,
): number {
  if (!isNarrowing(view)) return 0;
  return (
    filledOrderLineVideos(script).length - opsTrackerVideos(script, view).length
  );
}

/**
 * Orders and lines the ops tracker opens while a Status filter is on. Computed
 * at render and never stored, so clearing the filter leaves the user's own state.
 */
export function opsTrackerStatusFilterExpansion(
  orders: readonly { id: string; scripts: readonly OrderScriptRow[] }[],
  statusFilterActive: boolean,
): { orderIds: ReadonlySet<string>; scriptIds: ReadonlySet<string> } {
  if (!statusFilterActive) {
    return { orderIds: new Set(), scriptIds: new Set() };
  }
  return {
    orderIds: new Set(orders.map((order) => order.id)),
    scriptIds: new Set(
      orders.flatMap((order) => order.scripts.map((script) => script.id)),
    ),
  };
}

/**
 * A status change returns the order without the Status filter's match flags.
 * Carry them over by line so the row keeps showing only what matched until the
 * list refetch brings fresh flags.
 */
export function keepStatusFilterMatches<T extends MarketplaceOrder>(
  current: Pick<MarketplaceOrder, "scripts">,
  updated: T,
): T {
  const matches = new Map(
    current.scripts.flatMap((script) =>
      script.statusFilterMatch ? [[script.id, script.statusFilterMatch] as const] : [],
    ),
  );
  if (matches.size === 0) return updated;
  return {
    ...updated,
    scripts: updated.scripts.map((script) =>
      script.statusFilterMatch || !matches.has(script.id)
        ? script
        : { ...script, statusFilterMatch: matches.get(script.id) },
    ),
  };
}

/**
 * Rows shown open: the user's own expand state, plus what a Status filter
 * opens, with the user's clicks under that filter applied last so an
 * auto-opened row can still be collapsed.
 */
export function resolveStatusFilterExpandedIds(
  stored: ReadonlySet<string>,
  autoExpanded: ReadonlySet<string>,
  overrides: ReadonlyMap<string, boolean>,
): ReadonlySet<string> {
  if (autoExpanded.size === 0 && overrides.size === 0) return stored;
  const expanded = new Set([...stored, ...autoExpanded]);
  for (const [id, isExpanded] of overrides) {
    if (isExpanded) expanded.add(id);
    else expanded.delete(id);
  }
  return expanded;
}
